// DeepSeek API service for Knowledge Graph Analysis
const DEEPSEEK_API_KEY = 'sk-04a8bc1279a0475eaab02f745561042b';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/chat/completions';

// DeepSeek API Tariff (as provided by user)
const DEEPSEEK_TARIFF = {
  standard: {
    // 标准时段价格 (北京时间 08:30-00:30)
    deepseek_chat: {
      input_cached: 0.5,      // 百万tokens输入（缓存命中）
      input_uncached: 2.0,    // 百万tokens输入（缓存未命中）
      output: 8.0             // 百万tokens输出
    },
    deepseek_reasoner: {
      input_cached: 1.0,      // 百万tokens输入（缓存命中）
      input_uncached: 4.0,    // 百万tokens输入（缓存未命中）
      output: 16.0            // 百万tokens输出
    }
  },
  discount: {
    // 优惠时段价格 (北京时间 00:30-08:30)
    deepseek_chat: {
      input_cached: 0.25,     // 百万tokens输入（缓存命中）5折
      input_uncached: 1.0,    // 百万tokens输入（缓存未命中）5折
      output: 4.0             // 百万tokens输出 5折
    },
    deepseek_reasoner: {
      input_cached: 0.25,     // 百万tokens输入（缓存命中）2.5折
      input_uncached: 1.0,    // 百万tokens输入（缓存未命中）2.5折
      output: 4.0             // 百万tokens输出 2.5折
    }
  }
};

class DeepSeekKnowledgeGraphService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  // Calculate cost based on tokens and time
  calculateCost(inputTokens, outputTokens, model = 'deepseek-chat', isCached = false) {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000)); // UTC+8
    const hour = beijingTime.getHours();
    
    // Determine if it's discount time (00:30-08:30)
    const isDiscountTime = (hour >= 0 && hour < 8) || (hour >= 0 && hour < 8.5);
    const tariff = isDiscountTime ? DEEPSEEK_TARIFF.discount : DEEPSEEK_TARIFF.standard;
    
    const modelKey = model === 'deepseek-reasoner' ? 'deepseek_reasoner' : 'deepseek_chat';
    const inputRate = isCached ? tariff[modelKey].input_cached : tariff[modelKey].input_uncached;
    const outputRate = tariff[modelKey].output;
    
    const inputCost = (inputTokens / 1000000) * inputRate;
    const outputCost = (outputTokens / 1000000) * outputRate;
    
    return {
      inputCost: parseFloat(inputCost.toFixed(6)),
      outputCost: parseFloat(outputCost.toFixed(6)),
      totalCost: parseFloat((inputCost + outputCost).toFixed(6)),
      isDiscountTime,
      inputTokens,
      outputTokens,
      model
    };
  }

  // Check if current time is in discount period
  isDiscountTime() {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000)); // UTC+8
    const hour = beijingTime.getHours();
    return (hour >= 0 && hour < 8) || (hour >= 0 && hour < 8.5);
  }

  async analyzeKnowledgeGraph(text, language = 'traditional', onStatusUpdate = null) {
    const startTime = Date.now();
    let status = {
      timeElapsed: 0,
      tokensUsed: { input: 0, output: 0 },
      cost: { inputCost: 0, outputCost: 0, totalCost: 0 },
      phase: 'preparing',
      message: '準備分析...'
    };

    const updateStatus = (newStatus) => {
      status = { ...status, ...newStatus };
      if (onStatusUpdate) {
        onStatusUpdate(status);
      }
    };

    try {
      updateStatus({ phase: 'creating_prompt', message: '創建分析提示...' });
      const prompt = this.createKnowledgeGraphPrompt(text, language);
      
      updateStatus({ phase: 'calling_api', message: '調用 DeepSeek API...' });
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: 'You are an expert in Chinese text analysis and knowledge graph extraction. You analyze Chinese text to identify entities (characters, places, objects, concepts) and their relationships. You must respond with ONLY valid JSON format - no markdown, no explanations, no additional text. The response must be parseable by JSON.parse().'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.1, // Lower temperature for more consistent JSON output
          max_tokens: 4000,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek API Error Response:', errorText);
        throw new Error(`DeepSeek API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('DeepSeek Knowledge Graph API Response:', data);
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const content = data.choices[0].message.content;
      console.log('Raw content from Knowledge Graph API:', content);
      
      // Calculate tokens and cost
      const inputTokens = data.usage?.prompt_tokens || 0;
      const outputTokens = data.usage?.completion_tokens || 0;
      const totalTokens = data.usage?.total_tokens || 0;
      
      const costInfo = this.calculateCost(inputTokens, outputTokens, 'deepseek-chat', false);
      
      updateStatus({ 
        phase: 'processing_response', 
        message: '處理 API 響應...',
        tokensUsed: { input: inputTokens, output: outputTokens, total: totalTokens },
        cost: costInfo,
        timeElapsed: Date.now() - startTime
      });
      
      // Parse the JSON response with improved error handling
      let knowledgeGraphData;
      try {
        // First, try to parse the content directly
        knowledgeGraphData = JSON.parse(content);
      } catch (parseError) {
        console.error('Initial JSON Parse Error:', parseError);
        console.error('Raw content from API:', content);
        
        // Try to extract JSON from markdown code blocks
        try {
          const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
          if (jsonMatch && jsonMatch[1]) {
            const extractedJson = jsonMatch[1].trim();
            console.log('Extracted JSON from markdown:', extractedJson);
            knowledgeGraphData = JSON.parse(extractedJson);
          } else {
            // Try to find JSON-like content between curly braces
            const jsonLikeMatch = content.match(/\{[\s\S]*\}/);
            if (jsonLikeMatch) {
              console.log('Attempting to parse JSON-like content:', jsonLikeMatch[0]);
              knowledgeGraphData = JSON.parse(jsonLikeMatch[0]);
            } else {
              // If all parsing attempts fail, create a fallback response
              console.warn('All JSON parsing attempts failed, using fallback data');
              knowledgeGraphData = this.createFallbackKnowledgeGraphData(text);
            }
          }
        } catch (secondParseError) {
          console.error('Secondary JSON Parse Error:', secondParseError);
          console.error('Content that failed to parse:', content);
          
          // Create fallback data instead of throwing an error
          console.warn('Using fallback knowledge graph data due to parsing failure');
          knowledgeGraphData = this.createFallbackKnowledgeGraphData(text);
        }
      }
      
      updateStatus({ 
        phase: 'finalizing', 
        message: '完成知識圖譜生成...',
        timeElapsed: Date.now() - startTime
      });
      
      const result = this.processKnowledgeGraphData(knowledgeGraphData);
      
      updateStatus({ 
        phase: 'completed', 
        message: '分析完成！',
        timeElapsed: Date.now() - startTime
      });
      
      return {
        ...result,
        analysisInfo: {
          timeElapsed: Date.now() - startTime,
          tokensUsed: { input: inputTokens, output: outputTokens, total: totalTokens },
          cost: costInfo,
          isDiscountTime: this.isDiscountTime()
        }
      };
    } catch (error) {
      console.error('Error analyzing knowledge graph:', error);
      
      // Instead of throwing an error, create fallback data
      console.warn('API analysis failed, creating fallback knowledge graph data');
      
      const fallbackData = this.createFallbackKnowledgeGraphData(text);
      const result = this.processKnowledgeGraphData(fallbackData);
      
      updateStatus({ 
        phase: 'completed', 
        message: '使用備用數據完成分析',
        timeElapsed: Date.now() - startTime
      });
      
      return {
        ...result,
        analysisInfo: {
          timeElapsed: Date.now() - startTime,
          tokensUsed: { input: 0, output: 0, total: 0 },
          cost: { inputCost: 0, outputCost: 0, totalCost: 0, isDiscountTime: false, model: 'deepseek-chat' },
          isDiscountTime: this.isDiscountTime(),
          fallbackUsed: true
        }
      };
    }
  }

  createFallbackKnowledgeGraphData(text) {
    // Create a basic fallback knowledge graph when API parsing fails
    const words = text.split(/[\s\n，。！？；：""''（）【】]/).filter(word => word.length > 0);
    const uniqueWords = [...new Set(words)].slice(0, 10); // Take first 10 unique words
    
    const entities = {
      characters: [],
      places: [],
      objects: []
    };
    
    // Create basic entities from the text
    uniqueWords.forEach((word, index) => {
      if (word.length > 1) { // Only consider words with more than 1 character
        const entity = {
          id: `fallback_${index}`,
          name: word,
          type: index % 3 === 0 ? 'protagonist' : index % 3 === 1 ? 'origin' : 'life_force',
          description: `從文本中提取的實體: ${word}`,
          importance: Math.min(10, Math.max(1, Math.floor(Math.random() * 10) + 1))
        };
        
        if (index % 3 === 0) {
          entities.characters.push(entity);
        } else if (index % 3 === 1) {
          entities.places.push(entity);
        } else {
          entities.objects.push(entity);
        }
      }
    });
    
    // Create basic relationships
    const relationships = [];
    for (let i = 0; i < Math.min(entities.characters.length, 3); i++) {
      if (entities.places[i] && entities.objects[i]) {
        relationships.push({
          from: entities.characters[i].id,
          to: entities.places[i].id,
          type: '存在於',
          strength: Math.floor(Math.random() * 5) + 5
        });
        relationships.push({
          from: entities.characters[i].id,
          to: entities.objects[i].id,
          type: '使用',
          strength: Math.floor(Math.random() * 5) + 5
        });
      }
    }
    
    return {
      entities,
      relationships,
      summary: {
        totalEntities: entities.characters.length + entities.places.length + entities.objects.length,
        totalRelationships: relationships.length,
        mainThemes: ['文本分析', '實體提取'],
        keyInsights: '基於文本內容的基本實體和關係分析'
      }
    };
  }

  createKnowledgeGraphPrompt(text, language) {
    const langLabel = language === 'traditional' ? '繁體中文' : '簡體中文';
    
    return `請分析以下${langLabel}文本，提取知識圖譜的實體和關係。

文本內容：
${text}

請按照以下JSON格式返回分析結果，必須是有效的JSON格式，不要包含任何markdown格式：

{
  "entities": {
    "characters": [
      {
        "id": "unique_id",
        "name": "實體名稱",
        "type": "protagonist|helper|antagonist|supporting",
        "description": "實體描述",
        "importance": 1-10
      }
    ],
    "places": [
      {
        "id": "unique_id", 
        "name": "地點名稱",
        "type": "origin|destination|habitat|obstacle|dwelling|setting",
        "description": "地點描述",
        "importance": 1-10
      }
    ],
    "objects": [
      {
        "id": "unique_id",
        "name": "物品名稱", 
        "type": "life_force|essence|legacy|bond|theme|artifact|symbol",
        "description": "物品描述",
        "importance": 1-10
      }
    ]
  },
  "relationships": [
    {
      "from": "source_entity_id",
      "to": "target_entity_id", 
      "type": "關係類型（如：愛護、犧牲、融合、居住、使用等）",
      "strength": 1-10
    }
  ],
  "summary": {
    "totalEntities": 數字,
    "totalRelationships": 數字,
    "mainThemes": ["主題1", "主題2"],
    "keyInsights": "關鍵洞察"
  }
}

請確保：
1. 所有實體都有唯一的ID
2. 關係中的from和to對應實體ID
3. 重要性分數反映實體在文本中的重要性
4. 關係強度反映關係的緊密程度
5. 分析要深入且準確，捕捉文本的核心含義
6. 返回的內容必須是純JSON格式，不要包含任何markdown標記或額外的文字說明`;
  }

  processKnowledgeGraphData(data) {
    // Validate and process the API response
    if (!data.entities || !data.relationships) {
      throw new Error('Invalid knowledge graph data structure');
    }

    // Combine all entities into a single array with type information
    const allEntities = [
      ...(data.entities.characters || []).map(entity => ({ ...entity, category: 'character' })),
      ...(data.entities.places || []).map(entity => ({ ...entity, category: 'place' })),
      ...(data.entities.objects || []).map(entity => ({ ...entity, category: 'object' }))
    ];

    // Generate 3D positions for visualization
    const nodes = allEntities.map((entity, index) => {
      const angle = (index / allEntities.length) * 2 * Math.PI;
      const radius = 150 + Math.random() * 100;
      return {
        ...entity,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        z: (Math.random() - 0.5) * 100,
        radius: Math.max(8, entity.importance * 2)
      };
    });

    // Process relationships
    const links = (data.relationships || []).map(rel => ({
      ...rel,
      source: rel.from,
      target: rel.to,
      width: Math.max(1, rel.strength / 2)
    }));

    return {
      nodes,
      links,
      summary: data.summary || {
        totalEntities: allEntities.length,
        totalRelationships: links.length,
        mainThemes: [],
        keyInsights: ''
      }
    };
  }

  // Method to get mock data for demonstration
  getMockKnowledgeGraphData() {
    return {
      nodes: [
        {
          id: 'white_rabbit',
          name: '白兔',
          type: 'protagonist',
          category: 'character',
          description: '純潔的白兔，對月亮充滿好奇',
          importance: 10,
          x: 100,
          y: 50,
          z: 0,
          radius: 20
        },
        {
          id: 'old_man',
          name: '老爺爺',
          type: 'helper',
          category: 'character',
          description: '居住在月亮上的智慧老人',
          importance: 9,
          x: -80,
          y: 120,
          z: 20,
          radius: 18
        },
        {
          id: 'moon',
          name: '月亮',
          type: 'destination',
          category: 'place',
          description: '神秘的目的地，老爺爺的住所',
          importance: 10,
          x: 0,
          y: -100,
          z: 50,
          radius: 20
        },
        {
          id: 'earth',
          name: '地球',
          type: 'origin',
          category: 'place',
          description: '兔子們生活的家園',
          importance: 8,
          x: 150,
          y: -50,
          z: -30,
          radius: 16
        },
        {
          id: 'moonlight',
          name: '月光',
          type: 'life_force',
          category: 'object',
          description: '星球生命的光芒來源',
          importance: 9,
          x: -120,
          y: -80,
          z: 40,
          radius: 18
        },
        {
          id: 'sacrifice',
          name: '犧牲',
          type: 'theme',
          category: 'object',
          description: '愛與奉獻的核心主題',
          importance: 8,
          x: 80,
          y: 150,
          z: -20,
          radius: 16
        }
      ],
      links: [
        {
          from: 'white_rabbit',
          to: 'moon',
          type: '嚮往',
          strength: 10,
          source: 'white_rabbit',
          target: 'moon',
          width: 5
        },
        {
          from: 'white_rabbit',
          to: 'old_man',
          type: '遇見',
          strength: 9,
          source: 'white_rabbit',
          target: 'old_man',
          width: 4.5
        },
        {
          from: 'old_man',
          to: 'moon',
          type: '融合',
          strength: 9,
          source: 'old_man',
          target: 'moon',
          width: 4.5
        },
        {
          from: 'moonlight',
          to: 'earth',
          type: '照耀',
          strength: 9,
          source: 'moonlight',
          target: 'earth',
          width: 4.5
        },
        {
          from: 'white_rabbit',
          to: 'earth',
          type: '來自',
          strength: 7,
          source: 'white_rabbit',
          target: 'earth',
          width: 3.5
        }
      ],
      summary: {
        totalEntities: 6,
        totalRelationships: 5,
        mainThemes: ['犧牲', '愛', '承諾'],
        keyInsights: '故事探討了犧牲與愛的深刻主題，通過白兔的旅程展現了對未知的探索精神。'
      }
    };
  }
}

export default new DeepSeekKnowledgeGraphService(); 